/**
 * "Menu aanpassen": choose which items the sidebar's fixed menu shows and in
 * what order (drag, or the arrows). Synced to every device.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, GripVertical, RotateCcw } from 'lucide-react';
import { usePlugins } from '../plugins/runtime';
import {
  BUILT_IN_ITEMS,
  moveEntryBy,
  moveEntryTo,
  setHidden,
  useMenuEditor,
  useSidebarMenu,
  useUpdateSidebarMenu,
  type MenuEntry,
} from '../lib/sidebarMenu';
import { cn } from '../lib/cn';
import { Dialog } from './Dialog';
import { ModuleIcon } from './ModuleIcon';

const LABEL_KEY: Record<string, string> = {
  dashboard: 'dashboard.label',
  ai: 'ai.title',
  tasks: 'tasks.title',
  projects: 'projects.title',
  memos: 'memos.title',
  graph: 'graph.title',
  templates: 'templates.title',
  plugins: 'plugins.title',
  tips: 'tips.title',
};

export function SidebarMenuDialog() {
  const { t } = useTranslation();
  const { open, setOpen } = useMenuEditor();
  const plugins = usePlugins();
  const views = (plugins ?? [])
    .filter((p) => p.enabled)
    .flatMap((p) =>
      p.manifest.views.map((v) => ({
        id: `plugin:${p.pageId}:${v.id}`,
        title: v.title,
        icon: v.icon,
      })),
    );
  const available = [...BUILT_IN_ITEMS, ...views.map((v) => v.id)];
  const menu = useSidebarMenu(available);
  const update = useUpdateSidebarMenu(available);
  const [dragging, setDragging] = useState<string | null>(null);

  const label = (id: string) =>
    LABEL_KEY[id] ? t(LABEL_KEY[id]) : (views.find((v) => v.id === id)?.title ?? id);
  const moveBy = (id: string, delta: number) => void update((m) => moveEntryBy(m, id, delta));
  const toggle = (e: MenuEntry) => void update((m) => setHidden(m, e.id, !e.hidden));

  return (
    <Dialog open={open} onOpenChange={setOpen} title={t('sidebarMenu.title')}>
      <p className="mb-3 text-sm text-fg-muted">{t('sidebarMenu.intro')}</p>
      <ul className="flex flex-col gap-1" aria-label={t('sidebarMenu.title')}>
        {menu.map((e, i) => {
          const view = views.find((v) => v.id === e.id);
          return (
            <li
              key={e.id}
              draggable
              onDragStart={(ev) => {
                ev.dataTransfer.effectAllowed = 'move';
                ev.dataTransfer.setData('text/plain', e.id);
                setDragging(e.id);
              }}
              onDragOver={(ev) => {
                if (dragging === null) return;
                ev.preventDefault();
              }}
              onDrop={(ev) => {
                ev.preventDefault();
                if (dragging !== null) void update((m) => moveEntryTo(m, dragging, e.id));
                setDragging(null);
              }}
              onDragEnd={() => setDragging(null)}
              data-testid="menu-entry"
              className={cn(
                'flex items-center gap-2 rounded-xl border border-border bg-bg px-2 py-1.5',
                e.hidden && 'opacity-60',
                dragging === e.id && 'opacity-40',
              )}
            >
              <GripVertical className="size-4 shrink-0 cursor-grab text-fg-muted" aria-hidden />
              {view ? (
                <span aria-hidden className="mod-icon mod-plugins size-6 text-xs">
                  {view.icon ?? '🧩'}
                </span>
              ) : (
                <ModuleIcon kind={e.id} />
              )}
              <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-sm">
                <span className="min-w-0 flex-1 truncate">{label(e.id)}</span>
                <input
                  type="checkbox"
                  checked={!e.hidden}
                  onChange={() => toggle(e)}
                  aria-label={t('sidebarMenu.show', { label: label(e.id) })}
                  className="size-4 accent-[var(--accent)]"
                />
              </label>
              <button
                type="button"
                aria-label={t('sidebarMenu.up', { label: label(e.id) })}
                disabled={i === 0}
                onClick={() => moveBy(e.id, -1)}
                className="grid size-7 place-items-center rounded-full text-fg-muted hover:bg-bg-hover hover:text-fg disabled:opacity-30"
              >
                <ArrowUp className="size-3.5" />
              </button>
              <button
                type="button"
                aria-label={t('sidebarMenu.down', { label: label(e.id) })}
                disabled={i === menu.length - 1}
                onClick={() => moveBy(e.id, 1)}
                className="grid size-7 place-items-center rounded-full text-fg-muted hover:bg-bg-hover hover:text-fg disabled:opacity-30"
              >
                <ArrowDown className="size-3.5" />
              </button>
            </li>
          );
        })}
      </ul>
      <div className="mt-4 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => void update(() => null)}
          className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-fg-muted hover:bg-bg-hover hover:text-fg"
        >
          <RotateCcw className="size-3.5" />
          {t('sidebarMenu.reset')}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-full bg-accent px-4 py-1.5 text-sm font-medium text-accent-fg"
        >
          {t('sidebarMenu.done')}
        </button>
      </div>
    </Dialog>
  );
}
